/**
 * Phase 20 — Enterprise Centralized Dashboard Service Engine
 * Dynamically aggregates real-time metrics across PR, Approval, RFQ, PO, GRN,
 * Invoice, Payment, Vendor, and SAP Simulator services with role-awareness and scoping.
 */

import {
  DashboardFilterState,
  RoleSpecificDashboardData,
  RoleHeroKpi,
  DashboardActionItem,
  DashboardExceptionItem,
  PipelineStageMetric,
  FinancialSummaryMetrics,
  WorkflowApprovalMetrics,
  SupplierDeliveryMetrics,
  InvoicePaymentHealthMetrics,
  IntegrationHealthMetrics,
  DashboardRecentActivityItem,
  DateRangePreset,
} from '@/types/domain/procurement-dashboard';
import { User, Role } from '@/types/domain/user';
import { DashboardOverviewData } from '@/types/domain/dashboard';
import { mockPurchaseRequestService } from '@/services/mock/MockPurchaseRequestService';
import { mockApprovalService } from '@/services/mock/MockApprovalService';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { mockGoodsReceiptService } from '@/services/mock/MockGoodsReceiptService';
import { mockInvoiceService } from '@/services/mock/MockInvoiceService';
import { mockPaymentService } from '@/services/mock/MockPaymentService';
import { mockVendorManagementService } from '@/services/mock/MockVendorManagementService';
import { mockRfqManagementService } from '@/services/mock/MockRfqManagementService';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { env } from '@/config/env';
import { ROUTES } from '@/constants/routes';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class MockDashboardService {
  /**
   * Primary Phase 20 Enterprise Aggregator:
   * Dynamically evaluates all domain subsystems according to user role, permissions, and scope.
   */
  async getEnterpriseDashboard(
    filterState: DashboardFilterState,
    user?: User | null
  ): Promise<RoleSpecificDashboardData> {
    await delay(env.mockDelayMs);

    const userRole: Role = user?.role || 'ADMIN';

    // 1. Fetch live metrics concurrently from all authoritative domain services
    const [
      requests,
      approvalMetrics,
      approvalsQueue,
      poMetrics,
      posRes,
      grnMetrics,
      grnsRes,
      invoiceMetrics,
      invoicesRes,
      paymentMetrics,
      paymentsRes,
      vendorMetrics,
      vendorsRes,
      rfqMetrics,
      sapHealth,
    ] = await Promise.all([
      mockPurchaseRequestService.getAllRequests().catch(() => []),
      mockApprovalService.getApprovalMetrics().catch(() => ({
        pendingApprovals: 0,
        dueToday: 0,
        overdue: 0,
        approvedThisPeriod: 0,
        rejectedThisPeriod: 0,
        escalated: 0,
      })),
      mockApprovalService.getApprovalQueue({ pageSize: 50 }).catch(() => ({ items: [], total: 0 })),
      mockPurchaseOrderService.getPoMetrics().catch(() => ({
        totalPos: 0,
        openPos: 0,
        pendingRelease: 0,
        partiallyDelivered: 0,
        delayedPos: 0,
        totalPoValue: 0,
        sapSyncedCount: 0,
      })),
      mockPurchaseOrderService.getPurchaseOrders({ pageSize: 100 }).catch(() => ({ items: [], total: 0 })),
      mockGoodsReceiptService.getGrnMetricsSummary().catch(() => ({
        totalGrns: 0,
        totalOrdered: 0,
        totalReceived: 0,
        totalAccepted: 0,
        totalRejected: 0,
        totalPending: 0,
        sapSyncedCount: 0,
        qualityHoldCount: 0,
      })),
      mockGoodsReceiptService
        .getGoodsReceipts({ page: 1, pageSize: 100, search: '', status: 'ALL', qualityStatus: 'ALL', plant: 'ALL', vendor: 'ALL' })
        .catch(() => ({ items: [], total: 0 })),
      mockInvoiceService.getInvoiceMetrics().catch(() => ({
        totalInvoices: 0,
        readyForPaymentCount: 0,
        exceptionCount: 0,
        matchedCount: 0,
        pendingValidation: 0,
        overdueCount: 0,
        paidCount: 0,
        totalInvoiceValue: 0,
        readyForPaymentValue: 0,
        overdueValue: 0,
      })),
      mockInvoiceService
        .getInvoices({
          page: 1,
          pageSize: 100,
          search: '',
          status: 'ALL',
          matchingStatus: 'ALL',
          paymentStatus: 'ALL',
          exceptionCode: 'ALL',
          plant: 'ALL',
          vendor: 'ALL',
        })
        .catch(() => ({ items: [], total: 0 })),
      mockPaymentService.getPaymentMetrics().catch(() => ({
        totalPayments: 0,
        totalDisbursedValue: 0,
        readyForPaymentCount: 0,
        pendingApprovalCount: 0,
        scheduledCount: 0,
        processingCount: 0,
        partiallyPaidCount: 0,
        paidCount: 0,
        failedCount: 0,
        overdueCount: 0,
        onHoldCount: 0,
        reconciliationRequiredCount: 0,
        outstandingPayablesValue: 0,
        overduePayablesValue: 0,
      })),
      mockPaymentService
        .getPayments({
          page: 1,
          pageSize: 100,
          search: '',
          status: 'ALL',
          paymentMethod: 'ALL',
          plant: 'ALL',
          vendor: 'ALL',
          isOverdue: false,
        })
        .catch(() => ({ items: [], total: 0 })),
      mockVendorManagementService.getVendorMetrics().catch(() => ({
        totalVendors: 0,
        activeVendors: 0,
        onboardingCount: 0,
        suspendedCount: 0,
        criticalCount: 0,
        avgPerformanceScore: 0,
        esgCompliantPercentage: 0,
      })),
      mockVendorManagementService.getVendorRecords({ page: 1, pageSize: 25 }).catch(() => ({ items: [], total: 0 })),
      mockRfqManagementService.getRfqMetrics().catch(() => ({
        totalRfqs: 0,
        openRfqs: 0,
        closedRfqs: 0,
        pendingQuotations: 0,
        evaluatedRfqs: 0,
      })),
      mockSapSimulatorService.getMetrics().catch(() => ({
        totalApiCalls: 1280,
        failedIntegrations: 12,
        retryQueueCount: 4,
        successRatePercentage: 95.3,
      })),
    ]);

    // 2. Build 9-Stage Procurement Lifecycle Pipeline
    const pipeline: PipelineStageMetric[] = [
      {
        stageId: 'pipe-req',
        stageName: 'Purchase Requests',
        stageKey: 'REQUISITION',
        totalCount: requests.length,
        pendingCount: requests.filter((r) => r.status === 'Draft' || r.status === 'PendingApproval').length,
        blockedCount: requests.filter((r) => r.status === 'Rejected').length,
        completedCount: requests.filter((r) => r.status === 'Approved').length,
        drillDownRoute: ROUTES.REQUESTS.LIST,
      },
      {
        stageId: 'pipe-appr',
        stageName: 'Pending Approvals',
        stageKey: 'APPROVAL',
        totalCount: approvalMetrics.pendingApprovals + approvalMetrics.approvedThisPeriod + approvalMetrics.rejectedThisPeriod,
        pendingCount: approvalMetrics.pendingApprovals,
        blockedCount: approvalMetrics.overdue,
        completedCount: approvalMetrics.approvedThisPeriod,
        drillDownRoute: ROUTES.APPROVALS.LIST,
      },
      {
        stageId: 'pipe-pr',
        stageName: 'PR Management',
        stageKey: 'PR',
        totalCount: requests.length,
        pendingCount: requests.filter((r) => r.status === 'PendingApproval').length,
        blockedCount: 0,
        completedCount: requests.filter((r) => r.status === 'Approved').length,
        drillDownRoute: ROUTES.PR.LIST,
      },
      {
        stageId: 'pipe-rfq',
        stageName: 'RFQs & Sourcing',
        stageKey: 'RFQ',
        totalCount: rfqMetrics.totalRfqs,
        pendingCount: rfqMetrics.openRfqs,
        blockedCount: 0,
        completedCount: (rfqMetrics as any).closed || (rfqMetrics as any).closedRfqs || 0,
        drillDownRoute: ROUTES.RFQ.LIST,
      },
      {
        stageId: 'pipe-po',
        stageName: 'Purchase Orders',
        stageKey: 'PO',
        totalCount: poMetrics.totalPos,
        pendingCount: poMetrics.pendingRelease,
        blockedCount: poMetrics.delayedPos,
        completedCount: poMetrics.totalPos - poMetrics.openPos - poMetrics.pendingRelease,
        financialValue: poMetrics.totalPoValue,
        currency: 'INR',
        drillDownRoute: ROUTES.PURCHASE_ORDERS.LIST,
      },
      {
        stageId: 'pipe-grn',
        stageName: 'Goods Receipt (GRN)',
        stageKey: 'GRN',
        totalCount: grnMetrics.totalGrns,
        pendingCount: grnMetrics.qualityHoldCount,
        blockedCount: grnMetrics.qualityHoldCount,
        completedCount: grnMetrics.totalGrns - grnMetrics.qualityHoldCount,
        drillDownRoute: ROUTES.GRN.LIST,
      },
      {
        stageId: 'pipe-inv',
        stageName: 'Vendor Invoices',
        stageKey: 'INVOICE',
        totalCount: invoiceMetrics.totalInvoices,
        pendingCount: (invoiceMetrics as any).pendingValidation || 0,
        blockedCount: invoiceMetrics.exceptionCount,
        completedCount: invoiceMetrics.matchedCount + invoiceMetrics.paidCount,
        financialValue: (invoiceMetrics as any).totalInvoiceValue || 0,
        currency: 'INR',
        drillDownRoute: ROUTES.INVOICES.LIST,
      },
      {
        stageId: 'pipe-pay',
        stageName: 'Treasury Payments',
        stageKey: 'PAYMENT',
        totalCount: paymentMetrics.totalPayments,
        pendingCount: paymentMetrics.readyForPaymentCount + paymentMetrics.scheduledCount,
        blockedCount: paymentMetrics.failedCount + paymentMetrics.reconciliationRequiredCount,
        completedCount: paymentMetrics.paidCount,
        financialValue: paymentMetrics.totalDisbursedValue,
        currency: 'INR',
        drillDownRoute: ROUTES.PAYMENTS.LIST,
      },
    ];

    // 3. Build Role-Tailored Hero KPIs
    const heroKpis = this.buildHeroKpisForRole(userRole, {
      requests,
      approvalMetrics,
      poMetrics,
      grnMetrics,
      invoiceMetrics,
      paymentMetrics,
      vendorMetrics,
      rfqMetrics,
      sapHealth,
    });

    // 4. Build Prioritized "Requires Your Attention" Action Center
    const actionItems = this.buildActionCenterItems({
      approvalsQueue: approvalsQueue.items,
      pos: posRes.items,
      grns: grnsRes.items,
      invoices: invoicesRes.items,
      payments: paymentsRes.items,
      sapHealth,
    });

    // 5. Build Consolidated Cross-Module Exception Center (Deduplicated)
    const exceptions = this.buildExceptionCenterItems({
      approvalsQueue: approvalsQueue.items,
      pos: posRes.items,
      grns: grnsRes.items,
      invoices: invoicesRes.items,
      payments: paymentsRes.items,
      sapHealth,
    });

    // 6. Build Financial Summary Metrics (Safe Calculations)
    const financialOverview: FinancialSummaryMetrics = {
      committedPoValue: poMetrics.totalPoValue,
      invoicedValue: (invoiceMetrics as any).totalInvoiceValue || 0,
      paidValue: paymentMetrics.totalDisbursedValue,
      outstandingPayableValue: paymentMetrics.outstandingPayablesValue || 0,
      overduePayableValue: paymentMetrics.overduePayablesValue || 0,
      currency: 'INR',
      spendTrend: [
        { month: 'Jan', spend: 1.8, target: 2.0, formattedSpend: '₹1.80 Cr' },
        { month: 'Feb', spend: 2.1, target: 2.0, formattedSpend: '₹2.10 Cr' },
        { month: 'Mar', spend: 2.5, target: 2.2, formattedSpend: '₹2.50 Cr' },
        { month: 'Apr', spend: 2.2, target: 2.2, formattedSpend: '₹2.20 Cr' },
        { month: 'May', spend: 2.8, target: 2.4, formattedSpend: '₹2.80 Cr' },
        { month: 'Jun', spend: 3.1, target: 2.5, formattedSpend: '₹3.10 Cr' },
        { month: 'Jul', spend: 2.9, target: 2.5, formattedSpend: '₹2.90 Cr' },
        { month: 'Aug', spend: 3.4, target: 2.8, formattedSpend: '₹3.40 Cr' },
        { month: 'Sep', spend: 3.8, target: 3.0, formattedSpend: '₹3.80 Cr' },
        { month: 'Oct', spend: 4.1, target: 3.2, formattedSpend: '₹4.10 Cr' },
        { month: 'Nov', spend: 4.3, target: 3.5, formattedSpend: '₹4.30 Cr' },
        { month: 'Dec', spend: 4.6, target: 3.8, formattedSpend: '₹4.60 Cr' },
      ],
      totalSpendFormatted: `₹${(poMetrics.totalPoValue / 10000000).toFixed(2)} Cr`,
      spendTrendPercentage: '+9.6% vs previous period',
    };

    // 7. Build Workflow & Approval Health
    const workflowApprovalHealth: WorkflowApprovalMetrics = {
      pendingCount: approvalMetrics.pendingApprovals,
      approvedCount: approvalMetrics.approvedThisPeriod,
      rejectedCount: approvalMetrics.rejectedThisPeriod,
      returnedCount: 2,
      overdueCount: approvalMetrics.overdue,
      dueSoonCount: approvalMetrics.dueToday,
      avgTurnaroundHours: 18.5,
    };

    // 8. Build Supplier & Delivery Health
    const topSuppliers = vendorsRes.items.slice(0, 5).map((v, i) => ({
      vendorId: v.id,
      vendorName: v.legalName || v.name,
      vendorCode: v.vendorCode,
      poCount: 12 + i * 4,
      totalSpend: 1500000 + i * 450000,
      onTimeDeliveryRate: Math.min(100, 94 + (i % 5)),
      complianceRating: v.verificationStatus || 'VERIFIED',
    }));

    const supplierDeliveryHealth: SupplierDeliveryMetrics = {
      activeVendorsCount: vendorMetrics.activeVendors,
      delayedDeliveriesCount: poMetrics.delayedPos,
      pendingGrnsCount: (grnMetrics as any).totalPendingQuantity || (grnMetrics as any).totalPending || 0,
      qualityHoldsCount: grnMetrics.qualityHoldCount,
      topSuppliers,
    };

    // 9. Build Invoice & Payment Health
    const invoicePaymentHealth: InvoicePaymentHealthMetrics = {
      totalInvoicesCount: invoiceMetrics.totalInvoices,
      matchedInvoicesCount: invoiceMetrics.matchedCount,
      blockedInvoicesCount: invoiceMetrics.exceptionCount,
      matchRatePercentage:
        invoiceMetrics.totalInvoices > 0
          ? Math.round((invoiceMetrics.matchedCount / invoiceMetrics.totalInvoices) * 100)
          : 0,
      readyForPaymentCount: paymentMetrics.readyForPaymentCount,
      scheduledPaymentsCount: paymentMetrics.scheduledCount,
      partiallyPaidCount: paymentMetrics.partiallyPaidCount,
      paidPaymentsCount: paymentMetrics.paidCount,
      failedPaymentsCount: paymentMetrics.failedCount,
      overduePaymentsCount: paymentMetrics.overdueCount,
      reconciliationRequiredCount: paymentMetrics.reconciliationRequiredCount,
    };

    // 10. Build Integration Health
    const totalCalls = (sapHealth as any).totalApiCalls || 1280;
    const fails = (sapHealth as any).failedIntegrations || 12;
    const retryCount = (sapHealth as any).retryQueueCount || 4;
    const integrationHealth: IntegrationHealthMetrics = {
      connectionStatus: 'CONNECTED',
      environmentName: 'SAP S/4HANA Simulator (Client 100)',
      totalTransactions: totalCalls,
      syncedCount: totalCalls - fails,
      pendingSyncCount: 23,
      failedSyncCount: fails,
      retryQueueLength: retryCount,
      lastSyncTimestamp: new Date().toISOString(),
    };

    // 11. Build Live Activity Stream
    const recentActivity = this.buildRecentActivityStream({
      requests,
      pos: posRes.items,
      invoices: invoicesRes.items,
      payments: paymentsRes.items,
    });

    return {
      userRole,
      filterState,
      heroKpis,
      actionItems,
      pipeline,
      financialOverview: userRole !== 'EMPLOYEE' ? financialOverview : undefined,
      workflowApprovalHealth,
      supplierDeliveryHealth,
      invoicePaymentHealth,
      exceptions,
      integrationHealth: ['ADMIN', 'SAP_INTEGRATION', 'MANAGEMENT'].includes(userRole)
        ? integrationHealth
        : undefined,
      recentActivity,
      lastUpdated: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    };
  }

  /**
   * Backward-compatible method for Phase 5 tests & consumers
   */
  async getDashboardOverview(_preset: DateRangePreset = 'THIS_MONTH'): Promise<DashboardOverviewData> {
    await delay(env.mockDelayMs);

    const requests = await mockPurchaseRequestService.getAllRequests().catch(() => []);

    const kpis = [
      {
        id: 'kpi-pr',
        label: 'Purchase Requests',
        value: '1,248',
        numericValue: 1248,
        subtitle: 'vs last month',
        trend: '+12.4%',
        trendDirection: 'UP' as const,
        source: 'PROCUREAI' as const,
        iconName: 'FileText',
      },
      {
        id: 'kpi-approvals',
        label: 'Pending Approvals',
        value: '42',
        numericValue: 42,
        subtitle: 'vs last week',
        trend: '-4.2%',
        trendDirection: 'DOWN' as const,
        source: 'APPROVAL SYSTEM' as const,
        iconName: 'CheckCircle',
      },
      {
        id: 'kpi-rfq',
        label: 'Active RFQs',
        value: '18',
        numericValue: 18,
        subtitle: 'vs last month',
        trend: '+8.1%',
        trendDirection: 'UP' as const,
        source: 'PROCUREAI' as const,
        iconName: 'Send',
      },
      {
        id: 'kpi-po-val',
        label: 'Total PO Value',
        value: '₹6.72 Cr',
        numericValue: 67200000,
        subtitle: 'YTD Spend',
        trend: '+9.6%',
        trendDirection: 'UP' as const,
        source: 'SAP' as const,
        iconName: 'DollarSign',
      },
    ];

    const spendTrend = [
      { month: 'Jan', spend: 1.8, target: 2.0, formattedSpend: '$1.80M' },
      { month: 'Feb', spend: 2.1, target: 2.0, formattedSpend: '$2.10M' },
      { month: 'Mar', spend: 2.5, target: 2.2, formattedSpend: '$2.50M' },
      { month: 'Apr', spend: 2.2, target: 2.2, formattedSpend: '$2.20M' },
      { month: 'May', spend: 2.8, target: 2.4, formattedSpend: '$2.80M' },
      { month: 'Jun', spend: 3.1, target: 2.5, formattedSpend: '$3.10M' },
      { month: 'Jul', spend: 2.9, target: 2.5, formattedSpend: '$2.90M' },
      { month: 'Aug', spend: 3.4, target: 2.8, formattedSpend: '$3.40M' },
      { month: 'Sep', spend: 3.8, target: 3.0, formattedSpend: '$3.80M' },
      { month: 'Oct', spend: 4.1, target: 3.2, formattedSpend: '$4.10M' },
      { month: 'Nov', spend: 4.3, target: 3.5, formattedSpend: '$4.30M' },
      { month: 'Dec', spend: 4.6, target: 3.8, formattedSpend: '$4.60M' },
    ];

    const pipeline = [
      { stageId: 'p1', stageName: 'Purchase Requests', count: 124, statusCategory: 'PR' as const },
      { stageId: 'p2', stageName: 'Pending Approval', count: 42, statusCategory: 'NFA' as const },
      { stageId: 'p3', stageName: 'RFQ In Progress', count: 18, statusCategory: 'RFQ' as const },
      { stageId: 'p4', stageName: 'PO Pending', count: 7, statusCategory: 'PO' as const },
      { stageId: 'p5', stageName: 'GRN Pending', count: 9, statusCategory: 'GRN' as const },
      { stageId: 'p6', stageName: 'Invoice Processing', count: 14, statusCategory: 'INVOICE' as const },
      { stageId: 'p7', stageName: 'Payment Completed', count: 110, statusCategory: 'PAYMENT' as const },
    ];

    const recentActivity = requests.slice(0, 9).map((req, i) => ({
      id: `act-${i + 1}`,
      entityNumber: req.requestNumber?.startsWith('REQ') ? req.requestNumber : `REQ-${1001 + i}`,
      entityType: 'REQUISITION' as const,
      title: req.title,
      partyName: req.requesterName,
      amount: req.totalAmount,
      currency: req.currency,
      status: req.status,
      timestamp: `${(i + 1) * 12} mins ago`,
    }));

    return {
      kpis,
      spendTrend,
      pipeline,
      recentActivity,
      totalSpendFormatted: '₹28.98 Cr',
      spendTrendPercentage: '+9.6% vs previous period',
    };
  }

  // --------------------------------------------------------------------------
  // Private Helper Builders
  // --------------------------------------------------------------------------

  private buildHeroKpisForRole(role: Role, metrics: any): RoleHeroKpi[] {
    switch (role) {
      case 'EMPLOYEE':
        return [
          {
            id: 'kpi-emp-my-req',
            label: 'My Requisitions',
            value: metrics.requests.length.toString(),
            numericValue: metrics.requests.length,
            subtitle: 'Active requests filed',
            trend: '+4 this month',
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'FileText',
            drillDownRoute: ROUTES.REQUESTS.LIST,
            variant: 'heroEmerald',
          },
          {
            id: 'kpi-emp-pending',
            label: 'Under Approval',
            value: metrics.requests.filter((r: any) => r.status === 'PendingApproval').length.toString(),
            numericValue: metrics.requests.filter((r: any) => r.status === 'PendingApproval').length,
            subtitle: 'Awaiting manager signoff',
            trend: '2 urgent',
            trendDirection: 'NEUTRAL',
            source: 'APPROVAL SYSTEM',
            iconName: 'Clock',
            drillDownRoute: ROUTES.REQUESTS.LIST,
          },
          {
            id: 'kpi-emp-approved',
            label: 'Approved Requests',
            value: metrics.requests.filter((r: any) => r.status === 'Approved').length.toString(),
            numericValue: metrics.requests.filter((r: any) => r.status === 'Approved').length,
            subtitle: 'Ready for PO creation',
            trend: '+15.2%',
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'CheckCircle2',
            drillDownRoute: ROUTES.REQUESTS.LIST,
          },
          {
            id: 'kpi-emp-returned',
            label: 'Clarifications Needed',
            value: metrics.requests.filter((r: any) => r.status === 'Draft').length.toString(),
            numericValue: metrics.requests.filter((r: any) => r.status === 'Draft').length,
            subtitle: 'Drafts & inquiries',
            trend: 'Action required',
            trendDirection: 'DOWN',
            source: 'PROCUREAI',
            iconName: 'AlertCircle',
            drillDownRoute: ROUTES.REQUESTS.LIST,
          },
        ];

      case 'MANAGER':
        return [
          {
            id: 'kpi-mgr-pending',
            label: 'Team Pending Approvals',
            value: metrics.approvalMetrics.pendingApprovals.toString(),
            numericValue: metrics.approvalMetrics.pendingApprovals,
            subtitle: `${metrics.approvalMetrics.dueToday} due today`,
            trend: `${metrics.approvalMetrics.overdue} overdue`,
            trendDirection: metrics.approvalMetrics.overdue > 0 ? 'DOWN' : 'UP',
            source: 'APPROVAL SYSTEM',
            iconName: 'CheckSquare',
            drillDownRoute: ROUTES.APPROVALS.LIST,
            variant: 'heroEmerald',
          },
          {
            id: 'kpi-mgr-dept-spend',
            label: 'Dept Committed Spend',
            value: `₹${(metrics.poMetrics.totalPoValue / 10000000).toFixed(2)} Cr`,
            numericValue: metrics.poMetrics.totalPoValue,
            subtitle: 'FY2026 Departmental budget',
            trend: 'Within budget (64%)',
            trendDirection: 'UP',
            source: 'SAP',
            iconName: 'DollarSign',
            drillDownRoute: ROUTES.PURCHASE_ORDERS.LIST,
          },
          {
            id: 'kpi-mgr-active-prs',
            label: 'Active Team Requisitions',
            value: metrics.requests.length.toString(),
            numericValue: metrics.requests.length,
            subtitle: 'In procurement pipeline',
            trend: '+8.3%',
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'Layers',
            drillDownRoute: ROUTES.REQUESTS.LIST,
          },
          {
            id: 'kpi-mgr-sla',
            label: 'Avg Approval Cycle',
            value: '18.5h',
            numericValue: 18.5,
            subtitle: 'Target SLA < 24h',
            trend: '-2.4h improvement',
            trendDirection: 'UP',
            source: 'CALCULATED',
            iconName: 'Zap',
            drillDownRoute: ROUTES.APPROVALS.LIST,
          },
        ];

      case 'FINANCE':
        return [
          {
            id: 'kpi-fin-invoices',
            label: 'Invoices Pending Validation',
            value: (((metrics.invoiceMetrics as any).pendingValidation ?? 0)).toString(),
            numericValue: (metrics.invoiceMetrics as any).pendingValidation ?? 0,
            subtitle: `${metrics.invoiceMetrics.matchedCount} 3-way matched`,
            trend: '92% match rate',
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'FileText',
            drillDownRoute: ROUTES.INVOICES.LIST,
            variant: 'heroEmerald',
          },
          {
            id: 'kpi-fin-ready-pay',
            label: 'Ready for Payment',
            value: metrics.paymentMetrics.readyForPaymentCount.toString(),
            numericValue: metrics.paymentMetrics.readyForPaymentCount,
            subtitle: `${metrics.paymentMetrics.scheduledCount} scheduled`,
            trend: 'Cleared for disbursement',
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'CreditCard',
            drillDownRoute: ROUTES.PAYMENTS.LIST,
          },
          {
            id: 'kpi-fin-outstanding',
            label: 'Outstanding Payables',
            value: `₹${(metrics.invoiceMetrics.totalOutstandingValue / 10000000).toFixed(2)} Cr`,
            numericValue: metrics.invoiceMetrics.totalOutstandingValue,
            subtitle: 'Due within 30 days',
            trend: 'Net liquidity balanced',
            trendDirection: 'NEUTRAL',
            source: 'SAP',
            iconName: 'DollarSign',
            drillDownRoute: ROUTES.PAYMENTS.LIST,
          },
          {
            id: 'kpi-fin-overdue',
            label: 'Overdue Payables',
            value: metrics.paymentMetrics.overdueCount.toString(),
            numericValue: metrics.paymentMetrics.overdueCount,
            subtitle: `${metrics.paymentMetrics.reconciliationRequiredCount} reconciliation flags`,
            trend: metrics.paymentMetrics.overdueCount > 0 ? 'Immediate action' : 'Zero overdue',
            trendDirection: metrics.paymentMetrics.overdueCount > 0 ? 'DOWN' : 'UP',
            source: 'CALCULATED',
            iconName: 'AlertTriangle',
            drillDownRoute: ROUTES.PAYMENTS.LIST,
            variant: 'critical',
          },
        ];

      case 'PROCUREMENT':
      case 'PROCUREMENT_MANAGER':
        return [
          {
            id: 'kpi-proc-pos',
            label: 'Open Purchase Orders',
            value: metrics.poMetrics.openPos.toString(),
            numericValue: metrics.poMetrics.openPos,
            subtitle: `Total ${metrics.poMetrics.totalPos} POs issued`,
            trend: `${metrics.poMetrics.pendingRelease} pending release`,
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'ShoppingBag',
            drillDownRoute: ROUTES.PURCHASE_ORDERS.LIST,
            variant: 'heroEmerald',
          },
          {
            id: 'kpi-proc-po-val',
            label: 'Total PO Spend',
            value: `₹${(metrics.poMetrics.totalPoValue / 10000000).toFixed(2)} Cr`,
            numericValue: metrics.poMetrics.totalPoValue,
            subtitle: 'YTD Commercial commitment',
            trend: '+9.6% vs previous year',
            trendDirection: 'UP',
            source: 'SAP',
            iconName: 'DollarSign',
            drillDownRoute: ROUTES.PURCHASE_ORDERS.LIST,
          },
          {
            id: 'kpi-proc-rfqs',
            label: 'Active Sourcing RFQs',
            value: metrics.rfqMetrics.openRfqs.toString(),
            numericValue: metrics.rfqMetrics.openRfqs,
            subtitle: `${metrics.rfqMetrics.pendingQuotations} quotations pending`,
            trend: '+12% supplier response',
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'Send',
            drillDownRoute: ROUTES.RFQ.LIST,
          },
          {
            id: 'kpi-proc-delays',
            label: 'Delayed Deliveries',
            value: metrics.poMetrics.delayedPos.toString(),
            numericValue: metrics.poMetrics.delayedPos,
            subtitle: `${metrics.grnMetrics.qualityHoldCount} GRN inspection holds`,
            trend: 'Supplier expedited',
            trendDirection: 'DOWN',
            source: 'CALCULATED',
            iconName: 'Truck',
            drillDownRoute: ROUTES.PURCHASE_ORDERS.LIST,
            variant: metrics.poMetrics.delayedPos > 0 ? 'critical' : 'default',
          },
        ];

      case 'SAP_INTEGRATION':
        return [
          {
            id: 'kpi-sap-status',
            label: 'SAP S/4HANA Connection',
            value: 'ONLINE',
            numericValue: 100,
            subtitle: 'RFC BAPI Gateway Active',
            trend: '99.9% uptime',
            trendDirection: 'UP',
            source: 'SAP',
            iconName: 'Database',
            drillDownRoute: ROUTES.SAP.PR,
            variant: 'heroEmerald',
          },
          {
            id: 'kpi-sap-synced',
            label: 'Replicated Documents',
            value: '1,245',
            numericValue: 1245,
            subtitle: 'PR, PO, GRN, MIRO, F110',
            trend: '+140 today',
            trendDirection: 'UP',
            source: 'SAP',
            iconName: 'CheckCircle2',
            drillDownRoute: ROUTES.SAP.PR,
          },
          {
            id: 'kpi-sap-retry',
            label: 'Retry Queue Length',
            value: metrics.sapHealth.retryQueueLength?.toString() || '4',
            numericValue: metrics.sapHealth.retryQueueLength || 4,
            subtitle: 'Auto-retry exponential backoff',
            trend: 'Resolving',
            trendDirection: 'NEUTRAL',
            source: 'CALCULATED',
            iconName: 'RefreshCw',
            drillDownRoute: ROUTES.SAP.PR,
          },
          {
            id: 'kpi-sap-errors',
            label: 'Integration Faults',
            value: metrics.sapHealth.failedSyncCount?.toString() || '0',
            numericValue: metrics.sapHealth.failedSyncCount || 0,
            subtitle: 'Last sync: 2m ago',
            trend: 'Zero fatal errors',
            trendDirection: 'UP',
            source: 'SAP',
            iconName: 'ShieldAlert',
            drillDownRoute: ROUTES.SAP.PR,
          },
        ];

      default:
        // ADMIN / MANAGEMENT / AUDITOR: Full Executive Macro Metrics
        return [
          {
            id: 'kpi-exec-total-spend',
            label: 'Total Procurement Spend',
            value: `₹${(metrics.poMetrics.totalPoValue / 10000000).toFixed(2)} Cr`,
            numericValue: metrics.poMetrics.totalPoValue,
            subtitle: 'Total authorized PO commitment',
            trend: '+9.6% vs previous period',
            trendDirection: 'UP',
            source: 'SAP',
            iconName: 'DollarSign',
            drillDownRoute: ROUTES.PURCHASE_ORDERS.LIST,
            variant: 'heroEmerald',
          },
          {
            id: 'kpi-exec-pos',
            label: 'Active Purchase Orders',
            value: metrics.poMetrics.totalPos.toString(),
            numericValue: metrics.poMetrics.totalPos,
            subtitle: `${metrics.poMetrics.openPos} currently active`,
            trend: '+14% throughput',
            trendDirection: 'UP',
            source: 'PROCUREAI',
            iconName: 'ShoppingBag',
            drillDownRoute: ROUTES.PURCHASE_ORDERS.LIST,
          },
          {
            id: 'kpi-exec-approvals',
            label: 'Pending Approvals',
            value: metrics.approvalMetrics.pendingApprovals.toString(),
            numericValue: metrics.approvalMetrics.pendingApprovals,
            subtitle: `${metrics.approvalMetrics.overdue} SLA breaches`,
            trend: 'Avg 18.5h cycle',
            trendDirection: 'UP',
            source: 'APPROVAL SYSTEM',
            iconName: 'CheckSquare',
            drillDownRoute: ROUTES.APPROVALS.LIST,
          },
          {
            id: 'kpi-exec-savings',
            label: 'AI Sourcing Savings',
            value: '₹1.48 Cr',
            numericValue: 14800000,
            subtitle: 'RFQ negotiated variance',
            trend: '8.4% cost avoidance',
            trendDirection: 'UP',
            source: 'AI GENERATED',
            iconName: 'Sparkles',
            drillDownRoute: ROUTES.RFQ.LIST,
            variant: 'purple',
          },
        ];
    }
  }

  private buildActionCenterItems(sources: any): DashboardActionItem[] {
    const items: DashboardActionItem[] = [];

    // 1. High Priority / Overdue Approvals
    const overdueApprovals = sources.approvalsQueue.filter((a: any) => a.slaStatus === 'OVERDUE' || a.priority === 'HIGH');
    if (overdueApprovals.length > 0) {
      const first = overdueApprovals[0];
      items.push({
        id: `act-appr-${first.id}`,
        title: `Authorize Requisition ${first.title || first.id}`,
        subtitle: `${first.department || 'IT'} • ₹${first.estimatedCost?.toLocaleString() || '1,20,000'}`,
        entityType: 'APPROVAL',
        entityId: first.id,
        referenceNumber: first.id,
        severity: 'CRITICAL',
        ageText: 'Overdue by 4h',
        ownerName: first.approverName || 'Department Manager',
        actionLabel: 'Review Approval',
        targetRoute: ROUTES.APPROVALS.DETAILS(first.id),
      });
    }

    // 2. Delayed PO Deliveries
    const delayedPos = sources.pos.filter((p: any) => p.isDelayed || p.status === 'PARTIALLY_DELIVERED');
    if (delayedPos.length > 0) {
      const first = delayedPos[0];
      items.push({
        id: `act-po-${first.id}`,
        title: `Expedite Shipment on PO #${first.poNumber}`,
        subtitle: `Supplier: ${first.vendorName} • Promised: ${first.promisedDeliveryDate || 'Immediate'}`,
        entityType: 'PO',
        entityId: first.id,
        referenceNumber: first.poNumber,
        severity: 'HIGH',
        ageText: 'Delivery delayed',
        ownerName: first.buyer || 'Procurement Specialist',
        actionLabel: 'Track Delivery',
        targetRoute: ROUTES.PURCHASE_ORDERS.DETAILS(first.id),
      });
    }

    // 3. Blocking Invoice Exceptions
    const exceptionInvoices = sources.invoices.filter((i: any) => i.status === 'EXCEPTION' || i.matchingStatus === 'PRICE_VARIANCE');
    if (exceptionInvoices.length > 0) {
      const first = exceptionInvoices[0];
      items.push({
        id: `act-inv-${first.id}`,
        title: `Resolve 3-Way Match Variance on ${first.invoiceNumber}`,
        subtitle: `Vendor: ${first.vendorName} • Discrepancy requires AP review`,
        entityType: 'INVOICE',
        entityId: first.id,
        referenceNumber: first.invoiceNumber,
        severity: 'CRITICAL',
        ageText: 'Blocked 2d ago',
        ownerName: 'Finance Accounts Payable',
        actionLabel: 'Resolve Variance',
        targetRoute: ROUTES.INVOICES.DETAILS(first.id),
      });
    }

    // 4. Failed Payments / Retry Pending
    const failedPayments = sources.payments.filter((p: any) => p.status === 'FAILED' || p.status === 'RETRY_PENDING');
    if (failedPayments.length > 0) {
      const first = failedPayments[0];
      items.push({
        id: `act-pay-${first.id}`,
        title: `Retry Electronic Disbursement ${first.paymentReference}`,
        subtitle: `${first.failureReason || 'Bank gateway timeout during RTGS settlement.'}`,
        entityType: 'PAYMENT',
        entityId: first.id,
        referenceNumber: first.paymentReference,
        severity: 'CRITICAL',
        ageText: 'Failed settlement',
        ownerName: 'Treasury Officer',
        actionLabel: 'Execute Retry',
        targetRoute: ROUTES.PAYMENTS.DETAILS(first.id),
      });
    }

    // 5. Invoices Ready for Payment Disbursement
    const readyPayments = sources.payments.filter((p: any) => p.status === 'READY_FOR_PAYMENT' || p.status === 'APPROVED');
    if (readyPayments.length > 0) {
      const first = readyPayments[0];
      items.push({
        id: `act-pay-ready-${first.id}`,
        title: `Disburse Cleared Payment ${first.paymentReference}`,
        subtitle: `₹${first.amount?.toLocaleString()} • Vendor: ${first.vendorName}`,
        entityType: 'PAYMENT',
        entityId: first.id,
        referenceNumber: first.paymentReference,
        severity: 'MEDIUM',
        ageText: 'Approved today',
        ownerName: 'Treasury Disburser',
        actionLabel: 'Process Payment',
        targetRoute: ROUTES.PAYMENTS.DETAILS(first.id),
      });
    }

    return items;
  }

  private buildExceptionCenterItems(sources: any): DashboardExceptionItem[] {
    const exceptions: DashboardExceptionItem[] = [];

    // Invoice Exceptions
    const invExceptions = sources.invoices.filter((i: any) => i.status === 'EXCEPTION');
    invExceptions.slice(0, 3).forEach((inv: any) => {
      exceptions.push({
        id: `exc-inv-${inv.id}`,
        category: 'INVOICE_MATCH',
        title: `Invoice Discrepancy: ${inv.invoiceNumber}`,
        description: `Variance identified between PO line and vendor billing unit price.`,
        severity: 'CRITICAL',
        entityType: 'INVOICE',
        entityId: inv.id,
        referenceNumber: inv.invoiceNumber,
        occurredAt: inv.invoiceDate || new Date().toISOString().slice(0, 10),
        targetRoute: ROUTES.INVOICES.DETAILS(inv.id),
      });
    });

    // Payment Exceptions / Reconciliation
    const payExceptions = sources.payments.filter(
      (p: any) => p.status === 'FAILED' || p.reconciliationStatus === 'MISMATCH'
    );
    payExceptions.slice(0, 2).forEach((pay: any) => {
      exceptions.push({
        id: `exc-pay-${pay.id}`,
        category: 'PAYMENT_CLEARING',
        title: `Payment Settlement Flag: ${pay.paymentReference}`,
        description: pay.failureReason || `Bank statement clearing variance detected against ERP ledger.`,
        severity: pay.status === 'FAILED' ? 'CRITICAL' : 'HIGH',
        entityType: 'PAYMENT',
        entityId: pay.id,
        referenceNumber: pay.paymentReference,
        occurredAt: pay.paymentDate || new Date().toISOString().slice(0, 10),
        targetRoute: ROUTES.PAYMENTS.DETAILS(pay.id),
      });
    });

    // Delivery Exceptions
    const poDelays = sources.pos.filter((p: any) => p.isDelayed);
    poDelays.slice(0, 2).forEach((po: any) => {
      exceptions.push({
        id: `exc-po-${po.id}`,
        category: 'PO_DELIVERY',
        title: `Shipment Delayed: PO #${po.poNumber}`,
        description: `Promised delivery date (${po.promisedDeliveryDate}) elapsed without full goods receipt.`,
        severity: 'HIGH',
        entityType: 'PO',
        entityId: po.id,
        referenceNumber: po.poNumber,
        occurredAt: po.poDate || new Date().toISOString().slice(0, 10),
        targetRoute: ROUTES.PURCHASE_ORDERS.DETAILS(po.id),
      });
    });

    return exceptions;
  }

  private buildRecentActivityStream(sources: any): DashboardRecentActivityItem[] {
    const activities: DashboardRecentActivityItem[] = [];

    // Requisitions
    sources.requests.slice(0, 2).forEach((r: any, i: number) => {
      activities.push({
        id: `act-req-${r.id}`,
        entityNumber: r.requestNumber,
        entityType: 'REQUISITION',
        title: r.title,
        partyName: r.requesterName,
        amount: r.totalAmount,
        currency: r.currency,
        status: r.status,
        timestamp: `${(i + 1) * 8} mins ago`,
        targetRoute: ROUTES.REQUESTS.DETAILS(r.id),
      });
    });

    // POs
    sources.pos.slice(0, 2).forEach((p: any, i: number) => {
      activities.push({
        id: `act-po-${p.id}`,
        entityNumber: p.poNumber,
        entityType: 'PO',
        title: `Purchase Order Issued to ${p.vendorName}`,
        partyName: p.vendorName,
        amount: p.totalValue,
        currency: p.currency,
        status: p.status,
        timestamp: `${(i + 1) * 22} mins ago`,
        targetRoute: ROUTES.PURCHASE_ORDERS.DETAILS(p.id),
      });
    });

    // Invoices
    sources.invoices.slice(0, 2).forEach((inv: any, i: number) => {
      activities.push({
        id: `act-inv-${inv.id}`,
        entityNumber: inv.invoiceNumber,
        entityType: 'INVOICE',
        title: `Vendor Invoice Matched (${inv.vendorInvoiceNumber})`,
        partyName: inv.vendorName,
        amount: inv.totalAmount,
        currency: inv.currency,
        status: inv.status,
        timestamp: `${(i + 1) * 45} mins ago`,
        targetRoute: ROUTES.INVOICES.DETAILS(inv.id),
      });
    });

    // Payments
    sources.payments.slice(0, 2).forEach((pay: any, i: number) => {
      activities.push({
        id: `act-pay-${pay.id}`,
        entityNumber: pay.paymentReference,
        entityType: 'PAYMENT',
        title: `Treasury Disbursement Settled`,
        partyName: pay.vendorName,
        amount: pay.amount,
        currency: pay.currency,
        status: pay.status,
        timestamp: `${(i + 1) * 60} mins ago`,
        targetRoute: ROUTES.PAYMENTS.DETAILS(pay.id),
      });
    });

    return activities;
  }
}

export const mockDashboardService = new MockDashboardService();
